const DB_NAME = "EspoAppDB";
const DB_VERSION = 1.1;
const STORE_NAME = "cache";
const DRAFT_STORE_NAME = "drafts";

interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  expiry: number;
}

interface DraftEntry<T> {
  key: string;
  data: T;
  timestamp: number;
}

class EspoAppDB {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<IDBDatabase> | null = null;

  async init(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      if (!("indexedDB" in window)) {
        reject(new Error("IndexedDB не поддерживается"));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error("Не удалось открыть IndexedDB"));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: "key",
          });
          objectStore.createIndex("timestamp", "timestamp", { unique: false });
          objectStore.createIndex("expiry", "expiry", { unique: false });
        }

        if (!db.objectStoreNames.contains(DRAFT_STORE_NAME)) {
          const draftStore = db.createObjectStore(DRAFT_STORE_NAME, {
            keyPath: "key",
          });
          draftStore.createIndex("timestamp", "timestamp", { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          const entry: CacheEntry<T> | undefined = request.result;

          if (!entry) {
            resolve(null);
            return;
          }

          const now = Date.now();
          if (now > entry.expiry) {
            this.delete(key).catch(console.error);
            resolve(null);
            return;
          }

          resolve(entry.data);
        };

        request.onerror = () => {
          reject(new Error("Ошибка при чтении из IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при получении данных из EspoAppDB:", error);
      return null;
    }
  }

  async set<T>(
    key: string,
    data: T,
    expiryTime: number = 30 * 60 * 1000,
  ): Promise<void> {
    try {
      const db = await this.init();
      const now = Date.now();

      const entry: CacheEntry<T> = {
        key,
        data,
        timestamp: now,
        expiry: now + expiryTime,
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(entry);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error("Ошибка при записи в IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при записи в EspoAppDB:", error);
    }
  }

  async delete(key: string): Promise<void> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error("Ошибка при удалении из IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при удалении из EspoAppDB:", error);
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error("Ошибка при очистке IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при очистке EspoAppDB:", error);
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAllKeys();

        request.onsuccess = () => {
          resolve(request.result as string[]);
        };

        request.onerror = () => {
          reject(new Error("Ошибка при получении ключей из IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при получении ключей из EspoAppDB:", error);
      return [];
    }
  }

  async has(key: string): Promise<boolean> {
    const data = await this.get(key);
    return data !== null;
  }

  async getTimestamp(key: string): Promise<number | null> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          const entry: CacheEntry<any> | undefined = request.result;
          resolve(entry?.timestamp || null);
        };

        request.onerror = () => {
          reject(new Error("Ошибка при получении timestamp"));
        };
      });
    } catch (error) {
      console.error("Ошибка при получении timestamp:", error);
      return null;
    }
  }

  async cleanupExpired(): Promise<number> {
    try {
      const db = await this.init();
      const now = Date.now();
      let deletedCount = 0;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index("expiry");
        const range = IDBKeyRange.upperBound(now);
        const request = index.openCursor(range);

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve(deletedCount);
          }
        };

        request.onerror = () => {
          reject(new Error("Ошибка при очистке устаревших записей"));
        };
      });
    } catch (error) {
      console.error("Ошибка при очистке устаревших записей:", error);
      return 0;
    }
  }

  async deleteByPattern(pattern: string): Promise<number> {
    try {
      const keys = await this.getAllKeys();
      const keysToDelete = keys.filter((key) => key.includes(pattern));

      await Promise.all(keysToDelete.map((key) => this.delete(key)));
      return keysToDelete.length;
    } catch (error) {
      console.error("Ошибка при удалении по паттерну:", error);
      return 0;
    }
  }

  async getDraft<T>(key: string): Promise<DraftEntry<T> | null> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([DRAFT_STORE_NAME], "readonly");
        const store = transaction.objectStore(DRAFT_STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          const entry: DraftEntry<T> | undefined = request.result;
          resolve(entry || null);
        };

        request.onerror = () => {
          reject(new Error("Ошибка при чтении черновика из IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при получении черновика из EspoAppDB:", error);
      return null;
    }
  }

  async setDraft<T>(key: string, data: T): Promise<void> {
    try {
      const db = await this.init();
      const entry: DraftEntry<T> = {
        key,
        data,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([DRAFT_STORE_NAME], "readwrite");
        const store = transaction.objectStore(DRAFT_STORE_NAME);
        const request = store.put(entry);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error("Ошибка при записи черновика в IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при записи черновика в EspoAppDB:", error);
    }
  }

  async deleteDraft(key: string): Promise<void> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([DRAFT_STORE_NAME], "readwrite");
        const store = transaction.objectStore(DRAFT_STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error("Ошибка при удалении черновика из IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при удалении черновика из EspoAppDB:", error);
    }
  }

  async clearDrafts(): Promise<void> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([DRAFT_STORE_NAME], "readwrite");
        const store = transaction.objectStore(DRAFT_STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error("Ошибка при очистке черновиков в IndexedDB"));
        };
      });
    } catch (error) {
      console.error("Ошибка при очистке черновиков в EspoAppDB:", error);
    }
  }

  async getDraftKeys(): Promise<string[]> {
    try {
      const db = await this.init();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([DRAFT_STORE_NAME], "readonly");
        const store = transaction.objectStore(DRAFT_STORE_NAME);
        const request = store.getAllKeys();

        request.onsuccess = () => {
          resolve(request.result as string[]);
        };

        request.onerror = () => {
          reject(new Error("Ошибка при получении ключей черновиков"));
        };
      });
    } catch (error) {
      console.error("Ошибка при получении ключей черновиков:", error);
      return [];
    }
  }

  async getInfo(): Promise<{ totalKeys: number; size: number }> {
    try {
      const keys = await this.getAllKeys();
      return {
        totalKeys: keys.length,
        size: keys.length * 1024,
      };
    } catch (error) {
      console.error("Ошибка при получении информации о хранилище:", error);
      return { totalKeys: 0, size: 0 };
    }
  }
}

export const espoAppDB = new EspoAppDB();

if (typeof window !== "undefined") {
  espoAppDB.init().then(() => {
    espoAppDB.cleanupExpired().then((count) => {
      if (count > 0) {
        console.log(`EspoAppDB: очищено ${count} устаревших записей`);
      }
    });
  });
}
